import { KuxDms } from '@kaltura-ott/tvpil';
type VideoQualitySettingsType = KuxDms['bitrateLimitationManagement']['localVqSettings'];

/**
 * Return quality as a string from bitrate as a number
 * @param bitrate user selected bitrate as number
 * @param defaultCastQuality default quality fallback
 * @param videoQualitySettings array of quality and it's bitrates
 * @returns quality as a string 'HD', 'SD'
 */
export const getQualityFromBitrate = (
  bitrate: number,
  defaultCastQuality: string,
  videoQualitySettings: VideoQualitySettingsType,
) => {
  let response = defaultCastQuality;
  Object.keys(videoQualitySettings).map((key) => {
    if (videoQualitySettings[key]['top_range_bitrate'] === bitrate) {
      response = key.toUpperCase();
    }
  });
  return response;
};

export const sortFromLowestBitrate = (sortedVideoQualitySettings: VideoQualitySettingsType) => {
  return Object.entries(sortedVideoQualitySettings).sort(
    ([, a], [, b]) => a['top_range_bitrate'] - b['top_range_bitrate'],
  );
};

/**
 * Mapping quality string to key value. It converts 4K -> UHD.
 * @param quality as a string
 * @returns quality as a string
 */
export const keyToQualityStringMapping = (quality: string): string => (quality === '4K' ? 'UHD' : quality);

/**
 * Inverted function to qualityStringToKeyMapping. It converts UHD -> 4K.
 * @param quality as a string
 * @returns quality as a string
 */
export const qualityStringToKeyMapping = (quality: string): string => (quality === 'UHD' ? '4K' : quality);

/**
 * Return lowest possible quality from array of qualities
 * based on top_range_bitrate in this.dms.bitrateLimitationManagement.localVqSettings
 * @param input array of quality strings ['HD', 'SD', 'UHD']
 * @param defaultCastQuality fallback in case of mismatch
 * @param videoQualitySettings object cointaining key as quality name and top_range_bitrate as number
 * @returns quality as simple string 'HD', 'SD' ...
 */
export const getLowestQuality = (
  input: string[],
  defaultCastQuality: string,
  videoQualitySettings: VideoQualitySettingsType,
): string => {
  if (input.length === 0) {
    return defaultCastQuality;
  }

  // rebuild object where keys are sorted based on bitrate - keys will have order sd, hd, 4k...
  const sortedVideoQualitySettingsKey = Object.fromEntries(sortFromLowestBitrate(videoQualitySettings));

  let lowestQualityIndex = -1;

  for (let i = 0; i < input.length; i++) {
    const formattedKey = qualityStringToKeyMapping(input[i]).toLowerCase();
    const index = Object.keys(sortedVideoQualitySettingsKey).indexOf(formattedKey);
    if (index !== -1 && (lowestQualityIndex === -1 || index < lowestQualityIndex)) {
      lowestQualityIndex = index;
    }
  }

  if (lowestQualityIndex === -1) {
    return defaultCastQuality;
  }

  const lowestQualityKey = Object.keys(sortedVideoQualitySettingsKey)[lowestQualityIndex].toUpperCase();
  return keyToQualityStringMapping(lowestQualityKey);
};

/**
 * Return quality as a string based on household bandwidth and userBitrate
 * based on sequence diagram https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/3151535755/Multicast+Playing+Specification#Sequence-Diagram.1
 * @param householdCastQuality household quality as a string 'HD', 'SD' ...
 * @param userBitrate user bitrate as number
 * @param defaultCastQuality default fallback to quality as a string
 * @param localVqSettings object cointaining key as quality name and top_range_bitrate as number
 * @returns quality as a string 'HD', 'SD', 'UHD'
 */
export const getMulticastQualityValue = (
  householdCastQuality: string | undefined,
  userBitrate: number,
  defaultCastQuality: string,
  localVqSettings: VideoQualitySettingsType | undefined,
) => {
  if (householdCastQuality == null) {
    // userBitrate = 0 means AUTO and return HD
    if (userBitrate === 0) {
      return defaultCastQuality;
    }

    if (!localVqSettings) {
      // localVqSettings doesn't exist I can't compare bitrate values -> fallback and return HD
      return defaultCastQuality;
    }

    // localVqSettings exists I can use it for bitrate comparison
    // get quality 'HD', 'SD' etc. from exact bitrate otherwise fallback to defaultCastQuality
    const userCastQuality = getQualityFromBitrate(userBitrate, defaultCastQuality, localVqSettings);

    // compare qualities - [HD, user selected quality] and select the lowest value
    return getLowestQuality(['HD', userCastQuality], defaultCastQuality, localVqSettings);
  }

  if (!localVqSettings) {
    // localVqSettings doesn't exist I can't compare bitrate values -> fallback and return HD
    return defaultCastQuality;
  }

  // localVqSettings exists I can use it for bitrate comparison
  // get quality 'HD', 'SD' etc. from exact bitrate otherwise fallback to defaultCastQuality
  const userCastQuality = getQualityFromBitrate(userBitrate, defaultCastQuality, localVqSettings);

  // compare qualities - [household_bandwidth, user selected quality] and select the lowest value
  return getLowestQuality([householdCastQuality.toUpperCase(), userCastQuality], defaultCastQuality, localVqSettings);
};
